import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { format } from 'string-template';

import useToast from '../../../hooks/useToast';
import FacebookPixel from '../../../../Model/FacebookPixel';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import LoadingOverlay from '../../../components/LoadingOverlay';
import SaveButton from '../../../../components/Button/ActionButtons/SaveButton';
import {
  CardsContainer,
  Card,
  HeaderContainer,
  TitleContainer,
  Title,
  SubTitle,
  Container,
} from '../../../../components/v2/Card';
import InputLabel from '../../../../components/v2/InputLabel';
import { StyledTextField, StyledTextFieldContainer } from '../../../../components/Inputs';
import texts from './texts.json';

const FacebookPixelSettings = ({ coach }) => {
  const isComponentMountedRef = useComponentMounted();
  const [isLoading, setIsLoading] = useState(true);
  const [facebookPixelDoc, setFacebookPixelDoc] = useState(null);
  const [accessToken, setAccessToken] = useState('');
  const [pixelId, setPixelId] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      const pixelDoc = await FacebookPixel.getFacebookPixelConfigByCoachId(coach.id);
      if (isComponentMountedRef.current) {
        setFacebookPixelDoc(pixelDoc);
        const existingPixelId = pixelDoc?.pixelId;
        setPixelId(existingPixelId);
        setAccessToken(pixelDoc?.accessToken);
        setIsLoading(false);
      }
    };
    if (coach.id) {
      init();
    }
  }, [coach, isComponentMountedRef]);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await facebookPixelDoc.setFacebookPixelConfig(pixelId, accessToken);
      showToast(texts.pixelConfigUpdateSuccess, { success: true });
    } catch (error) {
      showToast(format(texts.pixelConfigUpdateFailure, { error: error.message }), { error: true });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CardsContainer $fullWidth>
      <Card>
        <HeaderContainer>
          <TitleContainer>
            <Title>{texts.facebookPixelConfig}</Title>
            <SubTitle>
              {texts.facebookPixelConfigDescription}
            </SubTitle>
          </TitleContainer>
        </HeaderContainer>

        <Container>
          <StyledTextFieldContainer $withMargin>
            <InputLabel>{texts.pixelId}</InputLabel>
            <StyledTextField
              value={pixelId}
              onChange={(e) => setPixelId(e.target.value)}
              placeholder={texts.pixelIdPlaceholder}
            />
          </StyledTextFieldContainer>

          <StyledTextFieldContainer $withMargin>
            <InputLabel>{texts.accessToken}</InputLabel>
            <StyledTextField
              type="password"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder={texts.accessTokenPlaceholder}
            />
          </StyledTextFieldContainer>
          <SaveButton
            onClick={handleSave}
            disabled={isLoading || !accessToken || !pixelId}
            $fitToContent
          >
            {texts.save}
          </SaveButton>
        </Container>
      </Card>
      {isLoading && <LoadingOverlay isLoading={isLoading} />}
    </CardsContainer>
  );
};

FacebookPixelSettings.propTypes = {
  coach: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
};

export default compose(observer)(FacebookPixelSettings);
